import React, { useState, useEffect } from 'react'
import Wrapper from '../../components/Wrapper'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  TablePagination,
  CircularProgress,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material'
import ViewListIcon from '@mui/icons-material/ViewList'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import PhoneIcon from '@mui/icons-material/Phone'
import AltRouteIcon from '@mui/icons-material/AltRoute'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import { menu, input, sip, did,  } from '../../translate'
import Api from '../../Api'
import { toast } from 'react-toastify'
import Cookies from 'js-cookie'
import Utils from '../../Utils'
import AssignedSearch from '../../components/new_component/AssignedSearch'
import { activitiesStyles, employeeTableStyles } from '../../helpers/style'

const color = '#7ebc0a'

function SIpProviders() {
  const navigate = useNavigate()
  const langIndex = +localStorage.getItem('atsLang') || 1
  const [viewMode, setViewMode] = useState('table')
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState(null)
  const [sortOrder, setSortOrder] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(
    Cookies.get('sip_providers') ? parseInt(Cookies.get('sip_providers'), 10) : 10
  )
  const [copied, setCopied] = useState(false)
  const [keepOnLeftMenu, setKeepOnLeftMenu] = useState(() => localStorage.getItem('sip_providers_keep_on_left_menu') === 'true')

  useEffect(() => {
    fetchProviders()
  }, [])

  const fetchProviders = async () => {
    setLoading(true)
    try {
      const response = await Api.getProvider('', 100, 1)
      if (response?.data?.status === 'ok') {
        const data = response.data.sip || response.data.data || []
        setProviders(data)
      }
    } catch (error) {
      console.error('Error fetching SIP providers:', error)
      toast.error('Error fetching SIP providers')
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (value) => {
    setSearchQuery(value)
  }

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
  }

  const filteredData = searchQuery
    ? providers.filter((item) => {
        const query = searchQuery.toLowerCase()
        const searchableFields = [
          item.username,
          item.redirect?.output,
          item.notes,
          item.in,
          item.out,
          item.lines,
          item.nodline,
        ]
        return searchableFields.some(
          (field) =>
            field != null && String(field).toLowerCase().includes(query)
        )
      })
    : providers

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortBy) return 0
    let aVal, bVal
    switch (sortBy) {
      case 'phone':
        aVal = a.username || ''
        bVal = b.username || ''
        break
      case 'forward':
        aVal = a.redirect?.output || ''
        bVal = b.redirect?.output || ''
        break
      case 'notes':
        aVal = a.notes || ''
        bVal = b.notes || ''
        break
      case 'active':
        aVal = `${a.in ?? 0}-${a.out ?? 0}`
        bVal = `${b.in ?? 0}-${b.out ?? 0}`
        break
      case 'date':
        aVal = a.inTime?.intime || ''
        bVal = b.inTime?.intime || ''
        break
      case 'line':
        aVal = a.lines ?? 0
        bVal = b.lines ?? 0
        break
      case 'no_line':
        aVal = a.nodline ?? 0
        bVal = b.nodline ?? 0
        break
      default:
        return 0
    }
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    }
    return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
  })

  const titleArr = [
    sip.phone[langIndex],
    sip.forward[langIndex],
    sip.note[langIndex],
    did.active[langIndex],
    did.date[langIndex],
    did.line[langIndex],
    did.no_line[langIndex],
  ]
  const descArr = sortedData.map((s) => ({
    phone: s.username || '',
    forward: s.redirect?.output || '',
    note: s.notes || '',
    active: `${s.in ?? 0} - ${s.out ?? 0}`,
    date: `${s.inTime?.intime || ''} - ${s.outTime?.outtime || ''}`,
    line: s.lines ?? 0,
    no_line: s.nodline ?? 0,
  }))

  const paginatedData =
    rowsPerPage > 0
      ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sortedData

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    Cookies.set('sip_providers', newRowsPerPage, { expires: 30 })
  }

  const handleAdd = () => {
    navigate('/sip_providers/add')
  }

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) setViewMode(newViewMode)
  }

  const handleEdit = (id) => {
    navigate(`/sip_providers/${id}`)
  }

  const handleDelete = async (id) => {
    const deleteText =
      langIndex === 1
        ? 'Are you sure you want to delete?'
        : langIndex === 2
          ? 'Вы уверены, что хотите удалить?'
          : 'Վստա՞հ եք, որ ցանկանում եք ջնջել'
    if (window.confirm(deleteText)) {
      try {
        await Api.deleteProvider(id)
        setProviders((prev) => prev.filter((item) => item.id !== id))
        toast.success('SIP provider deleted successfully')
      } catch (error) {
        console.error('Error deleting SIP provider:', error)
        toast.error('Error deleting SIP provider')
      }
    }
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  const renderProviderCard = (item) => {
    const isActive = +item.isactive === 2
    const displayName = item.username || item.notes || 'SIP Provider'
    return (
      <Card key={item.id} sx={activitiesStyles.activityCard}>
        <CardContent sx={activitiesStyles.cardContent}>
          <Box sx={activitiesStyles.cardContentBox}>
            <Avatar sx={activitiesStyles.getActivityAvatar(isActive)}>
              {displayName?.[0]?.toUpperCase() || 'S'}
            </Avatar>
            <Box sx={activitiesStyles.activityContentBox}>
              <Typography variant="h6" sx={activitiesStyles.activityTitle}>
                {displayName}
              </Typography>
              <Box sx={activitiesStyles.metadataBox}>
                {item.username && (
                  <Box sx={activitiesStyles.metadataItem}>
                    <PhoneIcon
                      fontSize="small"
                      sx={{ ...activitiesStyles.metadataIcon, mr: 0.5 }}
                    />
                    <Typography variant="caption" sx={activitiesStyles.metadataText}>
                      <strong>{sip.phone[langIndex]}:</strong> {item.username}
                    </Typography>
                  </Box>
                )}
                {item.redirect?.output && (
                  <Box sx={activitiesStyles.metadataItem}>
                    <Typography variant="caption" sx={activitiesStyles.metadataText}>
                      <strong>{sip.forward[langIndex]}:</strong> {item.redirect.output}
                    </Typography>
                  </Box>
                )}
                {item.notes && (
                  <Box sx={activitiesStyles.metadataItem}>
                    <Typography variant="caption" sx={activitiesStyles.metadataText}>
                      <strong>{sip.note[langIndex]}:</strong> {item.notes}
                    </Typography>
                  </Box>
                )}
                {(item.in !== undefined || item.out !== undefined) && (
                  <Box sx={activitiesStyles.metadataItem}>
                    <Typography variant="caption" sx={activitiesStyles.metadataText}>
                      <strong>{did.active[langIndex]}:</strong> {item.in ?? 0} - {item.out ?? 0}
                    </Typography>
                  </Box>
                )}
                {(item.inTime?.intime || item.outTime?.outtime) && (
                  <Box sx={activitiesStyles.metadataItem}>
                    <CalendarMonthIcon
                      fontSize="small"
                      sx={{ ...activitiesStyles.metadataIcon, mr: 0.5 }}
                    />
                    <Typography variant="caption" sx={activitiesStyles.metadataText}>
                      <strong>{did.date[langIndex]}:</strong>{' '}
                      {item.inTime?.intime || '-'} - {item.outTime?.outtime || '-'}
                    </Typography>
                  </Box>
                )}
                {item.lines !== undefined && (
                  <Box sx={activitiesStyles.metadataItem}>
                    <AltRouteIcon
                      fontSize="small"
                      sx={{ ...activitiesStyles.metadataIcon, mr: 0.5 }}
                    />
                    <Typography variant="caption" sx={activitiesStyles.metadataText}>
                      <strong>{did.line[langIndex]}:</strong> {item.lines}
                    </Typography>
                  </Box>
                )}
                {item.nodline ? (
                  <Box sx={activitiesStyles.metadataItem}>
                    <PhoneDisabledIcon
                      fontSize="small"
                      sx={{
                        ...activitiesStyles.metadataIcon,
                        mr: 0.5,
                        color: 'red',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ ...activitiesStyles.metadataText, color: 'red' }}
                    >
                      <strong>{did.no_line[langIndex]}:</strong> {item.nodline}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>
            <Box sx={employeeTableStyles.actionsContainer}>
              <Box sx={employeeTableStyles.actionButtonsBox}>
                <IconButton
                  size="small"
                  onClick={() => handleEdit(item.id)}
                  sx={employeeTableStyles.editIconButton}
                  aria-label="Edit"
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(item.id)}
                  sx={{ ...employeeTableStyles.deleteIconButton }}
                  aria-label="Delete"
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </IconButton>
              </Box>
              {isActive && (
                <CheckCircleIcon sx={employeeTableStyles.activeIcon(color)} />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Wrapper>
      <div className="tableParent" id="tableParent">
        <Paper sx={{ p: 3, boxShadow: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5, }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                sx={{ color: '#7ebc0a', fontSize: 18 }}
                onClick={handleGoBack}
                aria-label="Back"
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
              </IconButton>
              <Typography sx={{ fontWeight: 700, m: 0 }} variant="h5" component="h1">
                {menu.provide[langIndex]}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={keepOnLeftMenu}
                    onChange={(e) => {
                      const value = e.target.checked
                      setKeepOnLeftMenu(value)
                      localStorage.setItem('sip_providers_keep_on_left_menu', value ? 'true' : 'false')
                      if (value) {
                        localStorage.setItem('sip_providers', 'true')
                      } else {
                        localStorage.removeItem('sip_providers')
                      }
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#7ebc0a' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#7ebc0a' },
                    }}
                  />
                }
                label={langIndex === 1 ? 'Keep on the left menu' : langIndex === 2 ? 'Держать в левом меню' : 'Պահել ձախ մենյուում'}
              />
             
            </Box>
          </Box>
          <Box sx={employeeTableStyles.searchSortContainer}>
            <Box sx={employeeTableStyles.searchBox}>
              <AssignedSearch
                value={searchQuery}
                onChange={handleSearchChange}
                onAdd={handleAdd}
                placeholder={input.search?.[langIndex] || 'Search...'}
                showAdd={true}
              />
            </Box>
            <Box sx={employeeTableStyles.sortContainer}>
              <FormControl size="small" sx={employeeTableStyles.sortFormControl}>
                <InputLabel>{input.sortBy[langIndex] || 'Sort by'}</InputLabel>
                <Select
                  value={sortBy || ''}
                  label={input.sortBy[langIndex] || 'Sort by'}
                  onChange={(e) => {
                    if (e.target.value) {
                      handleSort(e.target.value)
                    } else {
                      setSortBy(null)
                      setSortOrder(null)
                    }
                  }}
                  sx={employeeTableStyles.sortSelect}
                >
                  <MenuItem value="">
                    <em>{input.none[langIndex] || 'None'}</em>
                  </MenuItem>
                  <MenuItem value="phone">{sip.phone[langIndex]}</MenuItem>
                  <MenuItem value="forward">{sip.forward[langIndex]}</MenuItem>
                  <MenuItem value="notes">{sip.note[langIndex]}</MenuItem>
                  <MenuItem value="active">{did.active[langIndex]}</MenuItem>
                  <MenuItem value="date">{did.date[langIndex]}</MenuItem>
                  <MenuItem value="line">{did.line[langIndex]}</MenuItem>
                  <MenuItem value="no_line">{did.no_line[langIndex]}</MenuItem>
                </Select>
              </FormControl>
              {sortBy && (
                <IconButton
                  size="small"
                  onClick={() => handleSort(sortBy)}
                  sx={employeeTableStyles.getSortIconButton(color)}
                  aria-label={sortOrder === 'asc' ? input.sortDesc[langIndex] || 'Sort descending' : input.sortAsc[langIndex] || 'Sort ascending'}
                >
                  {sortOrder === 'asc' ? (
                    <ArrowUpwardIcon />
                  ) : (
                    <ArrowDownwardIcon />
                  )}
                </IconButton>
              )}
            </Box>
          </Box>

          {loading ? (
            <Box sx={employeeTableStyles.loadingBox}>
              <CircularProgress sx={{ color }} />
            </Box>
          ) : paginatedData.length ? (
            <>
              {viewMode === 'table' ? (
                <Box sx={activitiesStyles.activitiesContainer}>
                  {paginatedData.map((item) => renderProviderCard(item))}
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {paginatedData.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      {renderProviderCard(item)}
                    </Grid>
                  ))}
                </Grid>
              )}
              <Box sx={employeeTableStyles.footerBox}>
                <Box className="footer-chip-box" sx={employeeTableStyles.footerLinksBox}>
                  <Chip
                    label="EXCEL"
                    variant="outlined"
                    size="small"
                    onClick={() => Utils.dataToExel(titleArr, descArr)}
                    sx={{ cursor: 'pointer', borderRadius: '7px', padding: '4px', fontSize: 11, '& .MuiChip-label': { fontSize: 11 }, '&:hover': { backgroundColor: '#7ebc0a', color: '#fff', borderColor: '#7ebc0a' } }}
                  />
                  <Chip
                    label="CSV"
                    variant="outlined"
                    size="small"
                    onClick={() => Utils.dataToExel(titleArr, descArr)}
                    sx={{ cursor: 'pointer', borderRadius: '7px', padding: '4px', fontSize: 11, '& .MuiChip-label': { fontSize: 11 }, '&:hover': { backgroundColor: '#7ebc0a', color: '#fff', borderColor: '#7ebc0a' } }}
                  />
                  <Chip
                    label="PRINT"
                    variant="outlined"
                    size="small"
                    onClick={() => window.print()}
                    sx={{ cursor: 'pointer', borderRadius: '7px', padding: '4px', fontSize: 11, '& .MuiChip-label': { fontSize: 11 }, '&:hover': { backgroundColor: '#7ebc0a', color: '#fff', borderColor: '#7ebc0a' } }}
                  />
                  <Chip
                    label="COPY"
                    variant="outlined"
                    size="small"
                    onClick={() => Utils.copy(setCopied)}
                    sx={{ cursor: 'pointer', borderRadius: '7px', padding: '4px', fontSize: 11, '& .MuiChip-label': { fontSize: 11 }, '&:hover': { backgroundColor: '#7ebc0a', color: '#fff', borderColor: '#7ebc0a' } }}
                  />
                </Box>
                <TablePagination
                  rowsPerPageOptions={[10, 18, 27, 36, { label: did.all[langIndex], value: -1 }]}
                  component="div"
                  count={sortedData.length}
                  labelRowsPerPage={input.rowsPerPage?.[langIndex] || 'Rows per page:'}
                  rowsPerPage={rowsPerPage === -1 ? sortedData.length : rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </>
          ) : (
            <Box sx={employeeTableStyles.emptyStateBox}>
              <Typography variant="body1" color="text.secondary">
                {searchQuery
                  ? did.no_found_provider_search[langIndex]
                  : did.no_found_provider[langIndex]}
              </Typography>
            </Box>
          )}
        </Paper>
      </div>
      {copied ? <div className="copied">Copied</div> : null}
    </Wrapper>
  )
}

export default SIpProviders
